import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import type { Season } from '@/types/season';
import type { SeasonTaskStats } from '@/services/dashboard-service';
import type { SeasonHuntStats } from '@/utils/countdown-utils';
import { daysUntilOpener, seasonStatus } from '@/utils/countdown-utils';
import { usePropertyStore } from '@/stores/property-store';
import { SEASON_TYPE_LABELS, deriveSeasonNameDisplay } from '@/utils/season-utils';

import { StartHuntButton } from './StartHuntButton';

interface CountdownWidgetProps {
  season: Season;
  taskStats: SeasonTaskStats;
  variant?: 'hero' | 'compact';
  /** Sit/harvest totals for the season — drives in-season sit count and post-season summary. */
  huntStats?: SeasonHuntStats;
}

/** Task progress text only — no visual bar. */
function TaskProgress({ taskStats }: { taskStats: SeasonTaskStats }) {
  const label =
    taskStats.total > 0
      ? `${taskStats.completed}/${taskStats.total} tasks complete`
      : 'Add tasks to track your prep';

  return (
    <p className="text-[10px] text-[#9a9590] mt-1" data-testid="task-progress">
      {label}
    </p>
  );
}

/** Subtitle above the numeral — season name + season type in caps */
function buildSubtitle(season: Season): string {
  const year = season.openerDate.slice(0, 4);
  const state = season.state.toUpperCase();
  const seasonTypeLabel = SEASON_TYPE_LABELS[season.seasonType].toUpperCase();
  // E.g. "2026 WI GUN DEER" — use the season name minus the year prefix if present
  return `${year} ${state} ${seasonTypeLabel}`;
}

// ---------------------------------------------------------------------------
// CountdownWidgetContent — the inner content WITHOUT the card shell.
// Consumed by SeasonHero directly so there is exactly ONE surface (the outer
// SeasonHero card) with no nested inner card border/shadow/bg.
//
// Public contract: named export, stable props interface. CountdownWidget
// (below) wraps this content in the standard card shell and preserves the
// existing public API byte-for-byte for all non-hero consumers.
// ---------------------------------------------------------------------------

export interface CountdownWidgetContentProps {
  season: Season;
  /** Sit/harvest totals — drives in-season sit count and post-season summary. */
  huntStats?: SeasonHuntStats;
}

/**
 * The raw countdown content: subtitle + numeral/state + label + optional sitSummary/summary.
 * Renders NO card shell (no bg/border/shadow/radius), NO task-progress subline.
 * SeasonHero provides the outer card surface and the §5 prep-link as the single prep prompt.
 * All other consumers use the wrapped `CountdownWidget` which adds the card shell + subline.
 */
export function CountdownWidgetContent({
  season,
  huntStats,
}: CountdownWidgetContentProps) {
  const navigate = useNavigate();
  const { properties } = usePropertyStore();
  const primaryPropertyId = properties.find((p) => p.isPrimary)?.id ?? null;

  const days = daysUntilOpener(season.openerDate);
  const status = seasonStatus(season);

  const isEnded = status === 'ended';
  const isOpenerToday = days === 0 && !isEnded;
  const isInSeason = days < 0 && !isEnded;
  const isNear = status === 'upcoming' && days <= 14;

  const openerFormatted = format(parseISO(season.openerDate), 'MMM d, yyyy');
  // openerFormatted is retained for API/behavior parity with CountdownWidget's upstream
  // shape even though the in-season countdown-label render was removed (issue #1122).
  void openerFormatted;

  // DASH-H2: the verbatim season name (title) + derived STATE · METHOD · YEAR (subLabel).
  // The subLabel REPLACES the legacy buildSubtitle render in every hero branch — the hero
  // owns exactly ONE subtitle line. buildSubtitle is retained only on the non-hero wrapper.
  const { title, subLabel } = deriveSeasonNameDisplay(season);

  // Title chrome rendered once at the top of each of the four branches (same DOM shape +
  // clamp class everywhere → constant title-block height by construction; AC#1/AC#8 whole-
  // card parity is device-verified, RULE-196). STATIC literal testid (data-testid).
  const titleBlock = (
    <div className="pt-4 text-center">
      <h2 className="season-hero-title" data-testid="season-hero-title">
        {title}
      </h2>
      <p className="field-label" data-testid="countdown-subtitle">
        {subLabel}
      </p>
    </div>
  );

  // ── START HUNT (opener day only) ─────────────────────────────────────────
  if (isOpenerToday) {
    const startHuntUrl = primaryPropertyId
      ? `/hunt/new?seasonId=${season.id}&propertyId=${primaryPropertyId}`
      : `/hunt/new?seasonId=${season.id}`;

    return (
      // Wrapper has no card styling — SeasonHero provides the outer surface.
      <div
        data-testid="countdown-widget"
        data-urgency="today"
        data-countdown-season-id={season.id}
        data-variant="hero"
      >
        {titleBlock}

        <div className="px-5">
          <StartHuntButton navigateUrl={startHuntUrl} />
        </div>

        <div className="pt-1 pb-2 text-center">
          <p
            className="text-[12px] font-medium tracking-wide text-[#c4c0bb]"
            data-testid="countdown-label"
          >
            SEASON OPENS TODAY
          </p>
          {/* No TaskProgress here — SeasonHero's §5 prep-link is the single prompt */}
        </div>
      </div>
    );
  }

  // ── SEASON ACTIVE (opener passed, season ongoing) ─────────────────────────
  if (isInSeason) {
    const dayNum = Math.abs(days) + 1;
    const sitSummary =
      huntStats && huntStats.totalSits > 0
        ? `${huntStats.totalSits} sit${huntStats.totalSits !== 1 ? 's' : ''} · ${huntStats.morningSits} morning · ${huntStats.eveningSits} evening`
        : null;

    return (
      <div
        data-testid="countdown-widget"
        data-urgency="in-season"
        data-countdown-season-id={season.id}
        data-variant="hero"
        className="px-5 py-3 text-center"
      >
        {titleBlock}
        <p
          className="font-display text-[64px] leading-none text-[#c8651a] mt-2"
          data-testid="countdown-numeral"
          data-urgency="in-season"
          data-hero-numeral="true"
        >
          Day {dayNum}
        </p>
        {sitSummary && (
          <p
            className="text-[10px] text-[#9a9590] mt-1"
            data-testid="sit-summary"
          >
            {sitSummary}
          </p>
        )}
      </div>
    );
  }

  // ── SEASON COMPLETE (ended) ──────────────────────────────────────────────
  if (isEnded) {
    const summaryNode = huntStats ? (
      <p
        className="text-[10px] text-[#9a9590] mt-1"
        data-testid="season-summary"
      >
        {`${huntStats.totalSits} sit${huntStats.totalSits !== 1 ? 's' : ''} · ${huntStats.totalHarvests} harvest${huntStats.totalHarvests !== 1 ? 's' : ''} · ${huntStats.daysAfield} day${huntStats.daysAfield !== 1 ? 's' : ''} afield`}
      </p>
    ) : null;

    return (
      <div
        data-testid="countdown-widget"
        data-urgency="off-season"
        data-countdown-season-id={season.id}
        data-variant="hero"
        className="px-5 py-3 text-center"
      >
        {titleBlock}
        <p
          className="text-[12px] font-medium tracking-wide text-[#c4c0bb] mt-2"
          data-testid="countdown-label"
        >
          SEASON COMPLETE
        </p>
        {summaryNode}
        {/* No TaskProgress here — SeasonHero's §5 prep-link is the single prompt */}
      </div>
    );
  }

  // ── UPCOMING ─────────────────────────────────────────────────────────────
  const urgency: 'normal' | 'near' = isNear ? 'near' : 'normal';

  // navigate is referenced for consistency with CountdownWidget's upstream shape
  void navigate;

  return (
    <div
      data-testid="countdown-widget"
      data-urgency={urgency}
      data-countdown-season-id={season.id}
      data-variant="hero"
      className="px-5 py-3 text-center"
    >
      {titleBlock}

      {/* Numeral: 64px DM Serif Display ember */}
      <p
        className="font-display text-[64px] leading-none text-[#c8651a] mt-2"
        data-testid="countdown-numeral"
        data-urgency={urgency}
        data-hero-numeral="true"
      >
        {days}
      </p>

      {/* Label: 12px medium secondary */}
      <p
        className="text-[12px] font-medium tracking-wide text-[#c4c0bb] mt-1"
        data-testid="countdown-label"
      >
        DAYS TO OPENER
      </p>
      {/* No TaskProgress here — SeasonHero's §5 prep-link is the single prompt */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CountdownWidget — the FULL CARD (shell + content + subline).
// Public API is UNCHANGED. Every non-hero consumer is byte-identical.
// SeasonHero uses CountdownWidgetContent directly to avoid double-wrap.
// ---------------------------------------------------------------------------

/**
 * Full countdown card: card shell (bg/border/shadow/radius) + content + task-progress subline.
 * Public API is stable and unchanged. Use `CountdownWidgetContent` in contexts that
 * already provide their own card surface (e.g. SeasonHero).
 */
export function CountdownWidget({
  season,
  taskStats,
  variant = 'compact',
  huntStats,
}: CountdownWidgetProps) {
  const navigate = useNavigate();
  const { properties } = usePropertyStore();
  const primaryPropertyId = properties.find((p) => p.isPrimary)?.id ?? null;

  const days = daysUntilOpener(season.openerDate);
  const status = seasonStatus(season);

  const isEnded = status === 'ended';
  const isOpenerToday = days === 0 && !isEnded;
  const isInSeason = days < 0 && !isEnded;
  const isNear = status === 'upcoming' && days <= 14;

  const isHero = variant === 'hero';
  const openerFormatted = format(parseISO(season.openerDate), 'MMM d, yyyy');
  const subtitle = buildSubtitle(season);

  // ── START HUNT (opener day only) ─────────────────────────────────────────
  if (isOpenerToday) {
    const startHuntUrl = primaryPropertyId
      ? `/hunt/new?seasonId=${season.id}&propertyId=${primaryPropertyId}`
      : `/hunt/new?seasonId=${season.id}`;

    return (
      <div
        className="bg-[#252320] border border-[#c8651a] rounded-lg overflow-hidden border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
        data-testid="countdown-widget"
        data-urgency="today"
        data-countdown-season-id={season.id}
        {...(isHero && { 'data-variant': 'hero' })}
      >
        {/* Subtitle */}
        <div className="px-5 pt-5 text-center">
          <p
            className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]"
            data-testid="countdown-subtitle"
          >
            {subtitle}
          </p>
        </div>

        <div className="px-5">
          <StartHuntButton navigateUrl={startHuntUrl} />
        </div>

        <div className="px-5 pb-4 pt-2">
          <p
            className="text-[12px] font-medium tracking-wide text-[#c4c0bb] text-center"
            data-testid="countdown-label"
          >
            SEASON OPENS TODAY
          </p>
          <TaskProgress taskStats={taskStats} />
        </div>
      </div>
    );
  }

  // ── SEASON ACTIVE (opener passed, season ongoing) ─────────────────────────
  if (isInSeason) {
    const dayNum = Math.abs(days) + 1;
    const sitSummary =
      huntStats && huntStats.totalSits > 0
        ? `${huntStats.totalSits} sit${huntStats.totalSits !== 1 ? 's' : ''} · ${huntStats.morningSits} morning · ${huntStats.eveningSits} evening`
        : null;

    return (
      <div
        className="bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg overflow-hidden px-5 py-4 text-center"
        data-testid="countdown-widget"
        data-urgency="in-season"
        data-countdown-season-id={season.id}
        {...(isHero && { 'data-variant': 'hero' })}
      >
        <p
          className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]"
          data-testid="countdown-subtitle"
        >
          SEASON ACTIVE
        </p>
        <p
          className="font-display text-[64px] leading-none text-[#c8651a] mt-2"
          data-testid="countdown-numeral"
          data-urgency="in-season"
          {...(isHero && { 'data-hero-numeral': 'true' })}
        >
          Day {dayNum}
        </p>
        <p
          className="text-[12px] font-medium tracking-wide text-[#c4c0bb] mt-1"
          data-testid="countdown-label"
        >
          {openerFormatted}
        </p>
        {sitSummary && (
          <p
            className="text-[10px] text-[#9a9590] mt-1"
            data-testid="sit-summary"
          >
            {sitSummary}
          </p>
        )}
      </div>
    );
  }

  // ── Standard countdown (ended or upcoming) ───────────────────────────────

  if (isEnded) {
    const summaryNode = huntStats ? (
      <p
        className="text-[10px] text-[#9a9590] mt-1"
        data-testid="season-summary"
      >
        {`${huntStats.totalSits} sit${huntStats.totalSits !== 1 ? 's' : ''} · ${huntStats.totalHarvests} harvest${huntStats.totalHarvests !== 1 ? 's' : ''} · ${huntStats.daysAfield} day${huntStats.daysAfield !== 1 ? 's' : ''} afield`}
      </p>
    ) : null;

    return (
      <div
        className="bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg px-5 py-4 text-center"
        data-testid="countdown-widget"
        data-urgency="off-season"
        data-countdown-season-id={season.id}
        {...(isHero && { 'data-variant': 'hero' })}
      >
        <p
          className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]"
          data-testid="countdown-subtitle"
        >
          {subtitle}
        </p>
        <p
          className="text-[12px] font-medium tracking-wide text-[#c4c0bb] mt-2"
          data-testid="countdown-label"
        >
          SEASON COMPLETE
        </p>
        {summaryNode}
        <TaskProgress taskStats={taskStats} />
      </div>
    );
  }

  // Upcoming seasons
  const urgency: 'normal' | 'near' = isNear ? 'near' : 'normal';

  // navigate is used in the SEASON ACTIVE and SEASON COMPLETE branches via inline onClick
  void navigate;

  return (
    <div
      className={[
        'bg-[#252320] border-[0.5px] border-[#2d2a26] shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-lg px-5 py-4 text-center',
        isNear ? 'border-[#c8651a]' : '',
      ].join(' ')}
      data-testid="countdown-widget"
      data-urgency={urgency}
      data-countdown-season-id={season.id}
      {...(isHero && { 'data-variant': 'hero' })}
    >
      {/* Subtitle: 10px bold caps muted */}
      <p
        className="text-[10px] font-bold uppercase tracking-widest text-[#9a9590]"
        data-testid="countdown-subtitle"
      >
        {subtitle}
      </p>

      {/* Numeral: 64px DM Serif Display ember */}
      <p
        className="font-display text-[64px] leading-none text-[#c8651a] mt-2"
        data-testid="countdown-numeral"
        data-urgency={urgency}
        {...(isHero && { 'data-hero-numeral': 'true' })}
      >
        {days}
      </p>

      {/* Label: 12px medium secondary */}
      <p
        className="text-[12px] font-medium tracking-wide text-[#c4c0bb] mt-1"
        data-testid="countdown-label"
      >
        DAYS TO OPENER
      </p>

      <TaskProgress taskStats={taskStats} />
    </div>
  );
}
